import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, onSnapshot, query, where, orderBy, limit, onAuthStateChanged } from '../services/firebaseImports';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../config/firebaseWithFailover';
import { User } from 'firebase/auth';
import Logger from '../services/logger';
import { DEFAULT_EQUIPMENT_TYPE } from '../constants/equipmentTypes';
import { LOAD_STATUS, PO_STATUS, SHIPPING_SCHEDULE_STATUS } from '../constants/statusValues';

export interface BrokerLoad {
  id: string;
  date: string;
  time: string;
  destination: string;
  pickup?: string;
  carrier: string;
  status: 'Open' | 'Carrier Pending' | 'Pending' | 'Active' | 'Completed' | 'Cancelled';
  type: string;
  shipTo?: string;
  cost: number;
  poNumber: string;
  brokerId: string;
  loadType: string;
  equipmentType: string;
  weight?: number;
  dimensions?: string;
  specialInstructions?: string;
}

interface LoadsContextType {
  loads: BrokerLoad[];
  loading: boolean;
  error: string | null;
  refreshLoads: () => Promise<void>;
  getLoadsByStatus: (status: BrokerLoad['status']) => BrokerLoad[];
  getLoadsByCarrier: (carrierId: string) => BrokerLoad[];
  getLoadsByDateRange: (startDate: Date, endDate: Date) => BrokerLoad[];
}

const LoadsContext = createContext<LoadsContextType | undefined>(undefined);

export const LoadsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loads, setLoads] = useState<BrokerLoad[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const auth = getFailoverAwareAuth();
  
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);
  const loadsListenerRef = React.useRef<(() => void) | null>(null);
  const debounceUpdate = React.useCallback((updateFn: () => void, delay: number = 300) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(updateFn, delay);
  }, []);

  const refreshLoads = async () => {
    if (!auth.currentUser) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    const statusMap: { [key: string]: BrokerLoad['status'] } = {
      'open': PO_STATUS.OPEN,
      'carrier pending': SHIPPING_SCHEDULE_STATUS.CARRIER_PENDING,
      'carrier pending/approval': SHIPPING_SCHEDULE_STATUS.CARRIER_PENDING,
      'pending': LOAD_STATUS.PENDING,
      'active': LOAD_STATUS.ACTIVE,
      'completed': LOAD_STATUS.COMPLETED,
      'cancelled': LOAD_STATUS.CANCELLED,
    };

    try {
      const db = getFailoverAwareDatabase();
      if (!auth.currentUser) {
        setLoading(false);
        return;
      }
      
      // FIXED: Add user filtering and limit to prevent unbounded query
      const brokerLoadsQuery = query(
        collection(db, 'brokerLoads'),
        where('brokerId', '==', auth.currentUser.uid),
        orderBy('date', 'desc'),
        limit(100) // Limit to 100 most recent loads per broker
      );
      const querySnapshot = await getDocs(brokerLoadsQuery);
      const loadsData = querySnapshot.docs
        .map(doc => {
          // No need to filter - query already filtered by brokerId
          const data = doc.data();
          const normalizedStatus = (data.status || '').toLowerCase();
          const status: BrokerLoad['status'] = statusMap[normalizedStatus] || PO_STATUS.OPEN;
          
          return {
            id: doc.id,
            date: data.date || '',
            time: data.scheduledTime || '',
            destination: data.deliveryLocation?.cityStateZip || data.destination || '',
            pickup: data.pickupLocation?.cityStateZip || data.origin || '',
            carrier: data.carrierId 
              ? (typeof data.carrier === 'object'
                  ? (data.carrier.companyName || data.carrier.id || 'TBD')
                  : (data.carrier || 'TBD'))
              : 'Unassigned',
            status,
            type: data.loadType || 'Full Load',
            shipTo: data.deliveryLocation?.name || '',
            cost: typeof data.rate === 'number' ? data.rate : (typeof data.total === 'number' ? data.total : 0),
            poNumber: data.poNumber || '',
            brokerId: data.brokerId || '',
            loadType: data.loadType || 'Full Load',
            equipmentType: data.equipmentType || DEFAULT_EQUIPMENT_TYPE,
            weight: data.weight,
            dimensions: data.dimensions,
            specialInstructions: data.specialInstructions,
          } as BrokerLoad;
        });
      setLoads(loadsData);
      setLoading(false);
    } catch (err: any) {
      Logger.error('Error refreshing loads:', err instanceof Error ? err : new Error(String(err)), { err });
      setError(err.message || 'Failed to refresh loads');
      setLoading(false);
    }
  };

  // FIXED: Memoize helper functions to prevent unnecessary recalculations (Phase 4)
  const getLoadsByStatus = useCallback((status: BrokerLoad['status']) => {
    return loads.filter(load => load.status === status);
  }, [loads]);

  const getLoadsByCarrier = useCallback((carrierId: string) => {
    return loads.filter(load => load.carrier === carrierId);
  }, [loads]);

  const getLoadsByDateRange = useCallback((startDate: Date, endDate: Date) => {
    return loads.filter(load => {
      const loadDate = new Date(load.date);
      return loadDate >= startDate && loadDate <= endDate;
    });
  }, [loads]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user: User | null) => {
      if (loadsListenerRef.current) {
        loadsListenerRef.current();
        loadsListenerRef.current = null;
      }
      if (user) {
        const db = getFailoverAwareDatabase();
        // FIXED: Add user filtering and limit to prevent unbounded real-time listener
        const brokerLoadsQuery = query(
          collection(db, 'brokerLoads'),
          where('brokerId', '==', user.uid),
          orderBy('date', 'desc'),
          limit(100) // Limit real-time listener to 100 most recent loads
        );
        const unsub = onSnapshot(brokerLoadsQuery, (querySnapshot) => {
          // FIXED: Debounce rapid updates to prevent excessive re-renders
          debounceUpdate(() => {
            try {
              setLoading(true);
              const statusMap: { [key: string]: BrokerLoad['status'] } = {
                'open': 'Open',
                'carrier pending': 'Carrier Pending',
                'carrier pending/approval': 'Carrier Pending',
                'active': 'Active',
                'completed': 'Completed',
                'cancelled': 'Cancelled',
              };

              const loadsData = querySnapshot.docs
                .map(doc => {
                  // No need to filter - query already filtered by brokerId
                  const data = doc.data();
                  const normalizedStatus = (data.status || '').toLowerCase();
                  const status: BrokerLoad['status'] = statusMap[normalizedStatus] || PO_STATUS.OPEN;
                  
                  return {
                    id: doc.id,
                    date: data.date || '',
                    time: data.scheduledTime || '',
                    destination: data.deliveryLocation?.cityStateZip || data.destination || '',
                    pickup: data.pickupLocation?.cityStateZip || data.origin || '',
                    carrier: data.carrierId 
                      ? (typeof data.carrier === 'object'
                          ? (data.carrier.companyName || data.carrier.id || 'TBD')
                          : (data.carrier || 'TBD'))
                      : 'Unassigned',
                    status,
                    type: data.loadType || 'Full Load',
                    shipTo: data.deliveryLocation?.name || '',
                    cost: typeof data.rate === 'number' ? data.rate : (typeof data.total === 'number' ? data.total : 0),
                    poNumber: data.poNumber || '',
                    brokerId: data.brokerId || '',
                    loadType: data.loadType || 'Full Load',
                    equipmentType: data.equipmentType || DEFAULT_EQUIPMENT_TYPE,
                    weight: data.weight,
                    dimensions: data.dimensions,
                    specialInstructions: data.specialInstructions,
                  } as BrokerLoad;
                });
              setLoads(loadsData);
              setLoading(false);
              setError(null);
            } catch (err: any) {
              Logger.error('Error processing load updates:', err instanceof Error ? err : new Error(String(err)), { err });
              setError(err.message || 'Failed to process load updates');
              setLoading(false);
            }
          }, 300); // 300ms debounce
        });
        loadsListenerRef.current = unsub;
      }
    });
    return () => {
      if (loadsListenerRef.current) {
        loadsListenerRef.current();
        loadsListenerRef.current = null;
      }
      unsubscribeAuth();
    };
  }, []);

  // Cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // FIXED: Memoize context value to prevent unnecessary re-renders (Phase 4)
  const value: LoadsContextType = useMemo(() => ({
    loads,
    loading,
    error,
    refreshLoads,
    getLoadsByStatus,
    getLoadsByCarrier,
    getLoadsByDateRange,
  }), [loads, loading, error, refreshLoads, getLoadsByStatus, getLoadsByCarrier, getLoadsByDateRange]);

  return (
    <LoadsContext.Provider value={value}>
      {children}
    </LoadsContext.Provider>
  );
};

export const useLoads = () => {
  const context = useContext(LoadsContext);
  if (context === undefined) {
    throw new Error('useLoads must be used within a LoadsProvider');
  }
  return context;
};
